import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import StatusPill from './StatusPill'
import { supabase } from '../lib/supabase'
import JobStockUsagePanel from './JobStockUsagePanel'
import {
  additionalWorkBillableTotal,
  additionalWorkNeedsCustomerExplanation,
  additionalWorkPrice,
  additionalWorkStatusLabel,
  additionalWorkStatusTone,
  canEditAdditionalWork,
  canSendAdditionalWorkApproval,
  isMissingAdditionalWorkSchemaError,
  normalisePartsMaterials,
} from '../lib/generalAdditionalWork'
import { formatServiceMoney, serviceCategoryLabel, servicePriceLabel } from '../lib/serviceMenu'
import {
  isMissingStockSchemaError,
  partsMaterialFromStockItem,
  stockItemSearchText,
  stockItemTitle,
  stockMoneyLabel,
  stockQuantity,
  stockQuantityLabel,
} from '../lib/stock'

function text(value) {
  return String(value || '').trim()
}

function fieldAmount(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number.toFixed(2) : ''
}

function displayDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

function evidenceTitle(item = {}) {
  return item.caption || item.file_name || 'Evidence'
}

function makeEmptyForm(source = {}) {
  return {
    id: '',
    source_type: source.source_type || 'manual',
    checklist_item_id: source.checklist_item_id || null,
    finding_id: source.finding_id || null,
    title: source.title || '',
    customer_explanation: source.customer_explanation || '',
    internal_note: source.internal_note || '',
    recommended_service_id: '',
    labour_minutes: '',
    labour_amount: '',
    parts_description: '',
    parts_amount: '',
    stock_materials: [],
    approval_required: true,
    customer_visible: true,
    status: 'draft',
    technician_completion_comment: '',
  }
}

function formFromRequest(row = {}) {
  const materials = normalisePartsMaterials(row.parts_materials)
  const stockMaterials = materials.filter(item => item.stock_item_id)
  const manualMaterials = materials.filter(item => !item.stock_item_id)
  return {
    id: row.id || '',
    source_type: row.source_type || 'manual',
    checklist_item_id: row.checklist_item_id || null,
    finding_id: row.finding_id || null,
    title: row.title || '',
    customer_explanation: row.customer_explanation || '',
    internal_note: row.internal_note || '',
    recommended_service_id: row.recommended_service_id || '',
    labour_minutes: row.labour_minutes ? String(row.labour_minutes) : '',
    labour_amount: fieldAmount(row.labour_amount),
    parts_description: manualMaterials.map(item => item.description).filter(Boolean).join('\n'),
    parts_amount: fieldAmount(Math.max(0, Number(row.parts_amount || 0) - stockMaterials.reduce((sum, item) => sum + item.amount, 0))),
    stock_materials: stockMaterials,
    approval_required: row.approval_required !== false,
    customer_visible: row.customer_visible !== false,
    status: row.status || 'draft',
    technician_completion_comment: row.technician_completion_comment || '',
  }
}

function sourceLabel(row = {}) {
  if (row.source_type === 'checklist_item') return 'Checklist item'
  if (row.source_type === 'finding') return 'Finding'
  return 'Manual'
}

export default function GeneralAdditionalWorkPanel({
  job,
  workshopId,
  services = [],
  mode = 'edit',
  sourceDraft = null,
  refreshKey = 0,
  customerEmail = '',
  onSourceConsumed,
  onChanged,
  onTotalsChange,
}) {
  const editable = mode === 'edit'
  const jobId = job?.id
  const vatInherited = Array.isArray(job?.service_snapshot) && job.service_snapshot.some(line => line.vat_source === true)
  const [vatPrices, setVatPrices] = useState(null)
  const [vatChoices, setVatChoices] = useState({ labour_treatment: '', manual_treatment: '' })
  const [vatBusy, setVatBusy] = useState(false)
  const vatSequence = useRef(0)
  const clearVatReview = () => { vatSequence.current++; setVatPrices(null); setVatBusy(false) }
  useEffect(() => { vatSequence.current++; setVatPrices(null); setVatBusy(false); return () => { vatSequence.current++ } }, [jobId])
  async function reviewVatPrice() {
    const sequence = ++vatSequence.current
    setVatPrices(null); setVatBusy(true); setError('')
    try {
      const { data, error: reviewError } = await supabase.rpc('workshop_vat_additional_prices', { p_job_id: jobId, p_request: requestPayload(), p_choices: vatChoices })
      if (reviewError) throw new Error(reviewError.message)
      if (!data?.tax?.complete) throw new Error('Reviewed Additional Work prices are unavailable.')
      if (sequence !== vatSequence.current) return
      setForm(current => ({ ...current, stock_materials: data.parts_materials.filter(item => item.stock_item_id) }))
      setVatPrices(data)
    } catch (err) { if (sequence === vatSequence.current) setError(err.message) }
    finally { if (sequence === vatSequence.current) setVatBusy(false) }
  }
  const [rows, setRows] = useState([])
  const [evidenceRows, setEvidenceRows] = useState([])
  const [linkedEvidence, setLinkedEvidence] = useState({})
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(() => makeEmptyForm())
  const [completionForms, setCompletionForms] = useState({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState('')
  const [sending, setSending] = useState('')
  const [missingSchema, setMissingSchema] = useState(false)
  const [stockItems, setStockItems] = useState([])
  const [stockSchemaMissing, setStockSchemaMissing] = useState(false)
  const [stockSearch, setStockSearch] = useState('')
  const [stockPick, setStockPick] = useState({ stock_item_id: '', quantity: '1' })
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const generalServices = useMemo(() => (
    (services || [])
      .filter(service => String(service?.module_key || 'suspension').toLowerCase() === 'general')
      .filter(service => service.active !== false)
      .slice()
      .sort((a, b) => {
        const categorySort = serviceCategoryLabel(a).localeCompare(serviceCategoryLabel(b))
        if (categorySort !== 0) return categorySort
        return String(a.name || '').localeCompare(String(b.name || ''))
      })
  ), [services])

  const filteredStockItems = useMemo(() => {
    const term = stockSearch.trim().toLowerCase()
    return (stockItems || [])
      .filter(item => item.active !== false && !item.archived_at)
      .filter(item => !term || stockItemSearchText(item).includes(term))
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  }, [stockItems, stockSearch])

  const load = useCallback(async () => {
    if (!jobId || !workshopId) {
      setRows([])
      setEvidenceRows([])
      setLinkedEvidence({})
      if (onTotalsChange) onTotalsChange(0)
      return
    }

    setLoading(true)
    setError('')
    try {
      const requestsResult = await supabase
        .from('job_additional_work_requests')
        .select('*')
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
        .order('created_at', { ascending: false })

      if (requestsResult.error) throw requestsResult.error

      const linksResult = await supabase
        .from('job_additional_work_evidence')
        .select('id, additional_work_request_id, evidence_id, active')
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
        .eq('active', true)

      if (linksResult.error) throw linksResult.error

      const evidenceResult = await supabase
        .from('job_evidence')
        .select('id, file_name, caption, evidence_type, visibility, stage, checklist_item_id, created_at')
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
        .order('created_at', { ascending: false })

      if (evidenceResult.error) throw evidenceResult.error

      const map = {}
      ;(linksResult.data || []).forEach(link => {
        const key = link.additional_work_request_id
        if (!map[key]) map[key] = new Set()
        map[key].add(link.evidence_id)
      })

      const requestRows = requestsResult.data || []
      setRows(requestRows)
      setEvidenceRows(evidenceResult.data || [])
      setLinkedEvidence(map)
      setMissingSchema(false)
      if (onTotalsChange) onTotalsChange(additionalWorkBillableTotal(requestRows))
    } catch (err) {
      if (isMissingAdditionalWorkSchemaError(err)) {
        setMissingSchema(true)
        setRows([])
        setEvidenceRows([])
        setLinkedEvidence({})
        if (onTotalsChange) onTotalsChange(0)
      } else {
        setError(err.message || 'Could not load additional work.')
      }
    } finally {
      setLoading(false)
    }
  }, [jobId, workshopId, onTotalsChange])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  useEffect(() => {
    let cancelled = false
    async function loadStockItems() {
      if (!workshopId || !editable) {
        setStockItems([])
        setStockSchemaMissing(false)
        return
      }
      try {
        const { data, error: stockError } = await supabase
          .from('stock_items')
          .select('*')
          .eq('workshop_id', workshopId)
          .eq('active', true)
          .order('category')
          .order('name')
        if (stockError) throw stockError
        if (!cancelled) {
          setStockItems(data || [])
          setStockSchemaMissing(false)
        }
      } catch (err) {
        if (!cancelled && isMissingStockSchemaError(err)) {
          setStockItems([])
          setStockSchemaMissing(true)
        }
      }
    }
    loadStockItems()
    return () => { cancelled = true }
  }, [workshopId, editable])

  useEffect(() => {
    if (!sourceDraft) return
    vatSequence.current++
    setVatPrices(null)
    setVatBusy(false)
    setEditing(true)
    setForm(makeEmptyForm(sourceDraft))
    setNotice('Additional work draft started from checklist item.')
    if (onSourceConsumed) onSourceConsumed()
  }, [sourceDraft, onSourceConsumed])

  function setField(key, value) {
    clearVatReview()
    setForm(current => ({ ...current, [key]: value }))
  }

  function startNew() {
    clearVatReview()
    setEditing(true)
    setForm(makeEmptyForm())
    setError('')
    setNotice('')
  }

  function startEdit(row) {
    clearVatReview()
    setVatChoices(row.vat_price_snapshot?.choices || { labour_treatment: '', manual_treatment: '' })
    setEditing(true)
    setForm(formFromRequest(row))
    setError('')
    setNotice('')
  }

  function cancelEdit() {
    clearVatReview()
    setEditing(false)
    setForm(makeEmptyForm())
    setError('')
  }

  function applyStockPickToForm() {
    clearVatReview()
    const item = stockItems.find(row => row.id === stockPick.stock_item_id)
    if (!item) {
      setError('Choose a stock item to add to the parts/materials estimate.')
      return
    }
    const material = { ...partsMaterialFromStockItem(item, stockQuantity(stockPick.quantity) || 1), description: stockItemTitle(item) }
    setForm(current => ({ ...current, stock_materials: [...current.stock_materials, { ...material, id: `stock-${Date.now()}-${current.stock_materials.length}` }] }))
    setNotice('Stock item added to the additional-work parts/materials estimate.')
  }

  function requestPayload() {
    const partsMaterials = normalisePartsMaterials([
      ...form.stock_materials,
      ...(
      text(form.parts_description)
        ? [{ description: form.parts_description, quantity: 1, amount: Number(form.parts_amount || 0) }]
        : []
      ),
    ])
    const recommendedService = generalServices.find(service => service.id === form.recommended_service_id)
    const labourAmount = Number(form.labour_amount || 0)
    const partsAmount = Number(form.parts_amount || 0) + form.stock_materials.reduce((sum, item) => sum + item.amount, 0)

    return {
      workshop_id: workshopId,
      job_id: jobId,
      source_type: form.source_type || 'manual',
      checklist_item_id: form.checklist_item_id || null,
      finding_id: form.finding_id || null,
      title: text(form.title),
      customer_explanation: text(form.customer_explanation),
      internal_note: form.internal_note || '',
      recommended_service_id: form.recommended_service_id || null,
      recommended_service_snapshot: recommendedService ? {
        id: recommendedService.id,
        module_key: 'general',
        name: recommendedService.name || '',
        category: recommendedService.category || '',
        category_label: serviceCategoryLabel(recommendedService),
        base_price: Number(recommendedService.base_price || 0),
        pricing_mode: recommendedService.pricing_mode || 'fixed',
        booking_type: recommendedService.booking_type || 'direct_bookable',
        expected_labour_minutes: Number(recommendedService.expected_labour_minutes || 0),
      } : {},
      labour_minutes: Number(form.labour_minutes || 0),
      labour_amount: Number.isFinite(labourAmount) ? labourAmount : 0,
      parts_materials: partsMaterials,
      parts_amount: Number.isFinite(partsAmount) ? partsAmount : 0,
      price_amount: (Number.isFinite(labourAmount) ? labourAmount : 0) + (Number.isFinite(partsAmount) ? partsAmount : 0),
      approval_required: Boolean(form.approval_required),
      customer_visible: Boolean(form.customer_visible),
      status: form.status || 'draft',
      technician_completion_comment: form.technician_completion_comment || '',
    }
  }

  async function saveDraft() {
    if (!jobId) {
      setError('Save the General job before adding additional work.')
      return
    }

    const payload = requestPayload()
    if (vatInherited && payload.price_amount > 0) {
      if (process.env.REACT_APP_VAT_BUILD3 !== 'true' || !vatPrices || vatBusy
        || Math.round(payload.price_amount * 100) !== vatPrices.tax.gross_minor) {
        setError('Review the Additional Work customer total and VAT before saving.')
        return
      }
      payload.vat_price_snapshot = vatPrices
    }
    if (!payload.title) {
      setError('Add a title for the additional work.')
      return
    }
    if (payload.status === 'awaiting_approval' && !payload.customer_explanation) {
      setError('Add a customer-facing explanation before sending approval.')
      return
    }

    setSaving('draft')
    setError('')
    setNotice('')
    try {
      const result = form.id
        ? await supabase
          .from('job_additional_work_requests')
          .update(payload)
          .eq('id', form.id)
          .eq('workshop_id', workshopId)
          .eq('job_id', jobId)
          .select('*')
          .single()
        : await supabase
          .from('job_additional_work_requests')
          .insert(payload)
          .select('*')
          .single()

      if (result.error) throw result.error
      await linkChecklistEvidence(result.data)
      setEditing(false)
      setForm(makeEmptyForm())
      await load()
      setNotice('Additional work saved.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not save additional work.')
    } finally {
      setSaving('')
    }
  }

  async function linkChecklistEvidence(savedRequest) {
    if (!savedRequest?.id || savedRequest.source_type !== 'checklist_item' || !savedRequest.checklist_item_id) return
    const checklistEvidence = evidenceRows.filter(item => item.checklist_item_id === savedRequest.checklist_item_id)
    if (!checklistEvidence.length) return
    const payload = checklistEvidence.map(item => ({
      workshop_id: workshopId,
      job_id: jobId,
      additional_work_request_id: savedRequest.id,
      evidence_id: item.id,
      active: true,
    }))
    const { error: linkError } = await supabase
      .from('job_additional_work_evidence')
      .upsert(payload, { onConflict: 'additional_work_request_id,evidence_id' })
    if (linkError) throw linkError
  }

  async function sendApproval(row) {
    if (!canSendAdditionalWorkApproval(row)) {
      setError('Add customer-facing explanation and price before sending approval.')
      return
    }

    setSending(row.id)
    setError('')
    setNotice('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before sending approval.')

      const res = await fetch('/.netlify/functions/send-general-additional-work-approval', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          requestId: row.id,
          to: customerEmail,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not send approval request.')
      await load()
      setNotice('Additional work approval link sent.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not send approval request.')
    } finally {
      setSending('')
    }
  }

  async function updateStatus(row, status) {
    if (['approved', 'declined', 'completed', 'not_completed'].includes(status)
      && row.customer_visible !== false
      && !text(row.customer_explanation)) {
      setError('Add a customer-facing explanation before making this additional work customer-visible or billable.')
      return
    }
    const completionComment = completionForms[row.id] || ''
    if (['completed', 'not_completed'].includes(status) && !text(completionComment)) {
      setError('Add a technician completion comment before marking this work complete or not completed.')
      return
    }

    setSaving(row.id)
    setError('')
    setNotice('')
    try {
      const { error: updateError } = await supabase
        .from('job_additional_work_requests')
        .update({
          status,
          technician_completion_comment: ['completed', 'not_completed'].includes(status)
            ? completionComment
            : row.technician_completion_comment || '',
        })
        .eq('id', row.id)
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
      if (updateError) throw updateError
      await load()
      setNotice(`Additional work marked ${additionalWorkStatusLabel(status).toLowerCase()}.`)
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not update additional work status.')
    } finally {
      setSaving('')
    }
  }

  async function voidRequest(row) {
    setSaving(row.id)
    setError('')
    setNotice('')
    try {
      const { error: updateError } = await supabase
        .from('job_additional_work_requests')
        .update({ status: 'voided' })
        .eq('id', row.id)
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
      if (updateError) throw updateError
      await load()
      setNotice('Additional work voided.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not void additional work.')
    } finally {
      setSaving('')
    }
  }

  async function toggleEvidence(row, evidence) {
    const linked = linkedEvidence[row.id]?.has(evidence.id)
    setSaving(`evidence-${row.id}-${evidence.id}`)
    setError('')
    try {
      if (linked) {
        const { error: updateError } = await supabase
          .from('job_additional_work_evidence')
          .update({ active: false })
          .eq('additional_work_request_id', row.id)
          .eq('evidence_id', evidence.id)
          .eq('workshop_id', workshopId)
        if (updateError) throw updateError
      } else {
        const { error: upsertError } = await supabase
          .from('job_additional_work_evidence')
          .upsert({
            workshop_id: workshopId,
            job_id: jobId,
            additional_work_request_id: row.id,
            evidence_id: evidence.id,
            active: true,
          }, { onConflict: 'additional_work_request_id,evidence_id' })
        if (upsertError) throw upsertError
      }
      await load()
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not update evidence link.')
    } finally {
      setSaving('')
    }
  }

  if (!jobId) {
    return (
      <>
        <div className="sec">Bespoke Additional Work</div>
        <div className="job-modal-callout" style={{ marginBottom: 14 }}>
          <div className="fs12 text-muted">Save the General job before adding additional work requests.</div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="sec">Bespoke Additional Work</div>
      <div className="job-modal-callout" style={{ marginBottom: 14 }}>
        <div className="section-head compact" style={{ marginBottom: 10 }}>
          <div>
            <div className="job-modal-callout-title">Bespoke additional work</div>
            <div className="text-muted fs12">Use this for one-off work outside the standard service menu. Menu services found mid-job belong in Extra Service Lines.</div>
          </div>
          {editable && <button className="btn btn-ghost btn-sm" type="button" onClick={startNew}>+ Add Work</button>}
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
        {notice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{notice}</div>}
        {missingSchema && <div className="alert alert-info">Additional work schema v84 has not been applied yet.</div>}
        {loading && <div className="fs12 text-muted">Loading additional work...</div>}

        {editing && editable && !missingSchema && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 14, background: 'var(--black)', marginBottom: 12 }}>
            <div className="grid-2">
              <div className="field">
                <label>Title</label>
                <input value={form.title} onChange={e => setField('title', e.target.value)} placeholder="e.g. Replace rear brake pads" />
              </div>
              <div className="field">
                <label>Recommended Service</label>
                <select value={form.recommended_service_id} onChange={e => setField('recommended_service_id', e.target.value)}>
                  <option value="">No linked service</option>
                  {generalServices.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} - {serviceCategoryLabel(service)} - {vatInherited ? 'Review customer total' : servicePriceLabel(service)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label>Customer-facing explanation</label>
              <textarea
                value={form.customer_explanation}
                onChange={e => setField('customer_explanation', e.target.value)}
                placeholder="Explain what is recommended and why in customer-friendly language."
                style={{ minHeight: 82 }}
              />
              {additionalWorkNeedsCustomerExplanation(form) && <div className="field-hint">Required before sending for customer approval or showing in a General report.</div>}
            </div>

            <div className="field">
              <label>Internal note</label>
              <textarea
                value={form.internal_note}
                onChange={e => setField('internal_note', e.target.value)}
                placeholder="Workshop-only note. This never appears in customer approval pages or reports."
                style={{ minHeight: 72 }}
              />
            </div>

            <div className="grid-3">
              <div className="field">
                <label>Labour Minutes</label>
                <input type="number" min="0" step="1" value={form.labour_minutes} onChange={e => setField('labour_minutes', e.target.value)} />
              </div>
              <div className="field">
                <label>{vatInherited ? 'Labour customer total, including VAT (£)' : 'Labour Amount (£)'}</label>
                <input type="number" min="0" step="0.01" value={form.labour_amount} onChange={e => setField('labour_amount', e.target.value)} placeholder="0.00" />
              </div>
              <div className="field">
                <label>{vatInherited ? 'Other materials customer total, including VAT (£)' : 'Other Parts / Materials Amount (£)'}</label>
                <input type="number" min="0" step="0.01" value={form.parts_amount} onChange={e => setField('parts_amount', e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div className="field">
              <label>Other Parts / Materials</label>
              <textarea
                value={form.parts_description}
                onChange={e => setField('parts_description', e.target.value)}
                placeholder="Pads, chain, cable, bearing kit..."
                style={{ minHeight: 62 }}
              />
            </div>

            {form.stock_materials.map((material, index) => (
              <div className="detail-row" key={material.id || index}>
                <span>{material.quantity} × {material.description} — {vatInherited && !vatPrices ? 'Review customer total' : formatServiceMoney(material.amount)}</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { clearVatReview(); setForm(current => ({ ...current, stock_materials: current.stock_materials.filter((_, i) => i !== index) })) }}>Remove</button>
              </div>
            ))}
            {vatInherited && <>
              <div className="grid-2">
                {[['labour_treatment', 'Labour VAT treatment'], ['manual_treatment', 'Other materials VAT treatment']].map(([key, label]) => <div className="field" key={key}>
                  <label>{label}</label>
                  <select value={vatChoices[key]} onChange={event => { clearVatReview(); setVatChoices(current => ({ ...current, [key]: event.target.value })) }}>
                    <option value="">Choose a treatment</option>
                    <option value="standard">Standard rate</option><option value="reduced">Reduced rate</option>
                    <option value="zero">Zero rate</option><option value="exempt">Exempt</option><option value="not_registered">Not VAT registered</option>
                  </select>
                </div>)}
              </div>
              <p className="fs12 text-muted">Stock uses each item's reviewed price and VAT treatment. Review the total after choosing labour and other material treatments.</p>
              <button type="button" className="btn btn-ghost btn-sm" disabled={vatBusy || process.env.REACT_APP_VAT_BUILD3 !== 'true'} onClick={reviewVatPrice}>{vatBusy ? 'Reviewing...' : 'Review Additional Work total'}</button>
              {vatPrices && <div>
                <div className="detail-row"><span>Subtotal</span><span>{formatServiceMoney(vatPrices.tax.net_minor / 100)}</span></div>
                <div className="detail-row"><span>VAT</span><span>{formatServiceMoney(vatPrices.tax.tax_minor / 100)}</span></div>
                <div className="detail-row"><span>Total</span><span>{formatServiceMoney(vatPrices.tax.gross_minor / 100)}</span></div>
              </div>}
            </>}
            {!stockSchemaMissing && stockItems.length > 0 && (
              <div className="stock-inline-helper">
                <div>
                  <div className="fs12 fw7 text-white">Populate from stock</div>
                  <div className="fs12 text-muted">Each stock item keeps its own quantity and estimate. Confirm actual stock use on the saved request below.</div>
                </div>
                <div className="grid-3">
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Search</label>
                    <input
                      type="search"
                      value={stockSearch}
                      onChange={e => setStockSearch(e.target.value)}
                      placeholder="Pads, chain, bearing..."
                    />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Stock Item</label>
                    <select
                      value={stockPick.stock_item_id}
                      onChange={e => setStockPick(current => ({ ...current, stock_item_id: e.target.value }))}
                    >
                      <option value="">Choose item...</option>
                      {filteredStockItems.map(item => (
                        <option key={item.id} value={item.id}>
                          {stockItemTitle(item)} · {stockQuantityLabel(item.quantity_on_hand, item.unit_type)} · {vatInherited ? 'Review customer total' : stockMoneyLabel(item.sell_price)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Qty</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={stockPick.quantity}
                        onChange={e => setStockPick(current => ({ ...current, quantity: e.target.value }))}
                      />
                      <button className="btn btn-ghost btn-sm" type="button" onClick={applyStockPickToForm}>Use</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid-3">
              <label className="check-row" style={{ alignSelf: 'center' }}>
                <input type="checkbox" checked={form.approval_required} onChange={e => setField('approval_required', e.target.checked)} /> Approval required
              </label>
              <label className="check-row" style={{ alignSelf: 'center' }}>
                <input type="checkbox" checked={form.customer_visible} onChange={e => setField('customer_visible', e.target.checked)} /> Customer visible
              </label>
              <div className="detail-row">
                <span className="detail-label">Additional amount</span>
                <span className="detail-val">{vatInherited && !vatPrices ? 'Review required' : formatServiceMoney(additionalWorkPrice(requestPayload()))}</span>
              </div>
            </div>

            <div className="action-row">
              <button className="btn btn-primary btn-sm" type="button" onClick={saveDraft} disabled={Boolean(saving)}>
                {saving === 'draft' ? 'Saving...' : form.id ? 'Save Additional Work' : 'Create Additional Work'}
              </button>
              <button className="btn btn-ghost btn-sm" type="button" onClick={cancelEdit} disabled={Boolean(saving)}>Cancel</button>
            </div>
          </div>
        )}

        {!loading && !rows.length && !editing && !missingSchema && (
          <div className="muted-empty">No additional work requests have been recorded yet.</div>
        )}

        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map(row => {
            const statusTone = additionalWorkStatusTone(row.status)
            const linked = linkedEvidence[row.id] || new Set()
            const canEdit = editable && canEditAdditionalWork(row)
            const canSend = editable && canSendAdditionalWorkApproval(row)
            const canMarkComplete = editable && ['approved'].includes(row.status)
            const canMarkNoApproval = editable && row.approval_required === false && row.status === 'draft'
            return (
              <div key={row.id} className="muted-empty" style={{ textAlign: 'left', borderStyle: 'solid' }}>
                <div className="section-head compact">
                  <div>
                    <div className="text-white fw7">{row.title}</div>
                    <div className="fs12 text-muted" style={{ marginTop: 3 }}>
                      {sourceLabel(row)} - {formatServiceMoney(additionalWorkPrice(row))}
                    </div>
                  </div>
                  <StatusPill tone={statusTone}>{additionalWorkStatusLabel(row.status)}</StatusPill>
                </div>
                {row.customer_explanation && <div className="fs12" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{row.customer_explanation}</div>}
                {row.internal_note && editable && <div className="fs12 text-muted" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>Internal: {row.internal_note}</div>}
                {row.customer_decision_at && (
                  <div className="fs12 text-muted" style={{ marginTop: 8 }}>
                    Customer responded {displayDateTime(row.customer_decision_at)}{row.customer_decision_note ? `: ${row.customer_decision_note}` : ''}
                  </div>
                )}
                {row.technician_completion_comment && (
                  <div className="fs12 text-muted" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                    Completion: {row.technician_completion_comment}
                  </div>
                )}

                {editable && (
                  <>
                    <div className="action-row" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
                      {canEdit && <button className="btn btn-ghost btn-sm" type="button" onClick={() => startEdit(row)}>Edit Draft</button>}
                      {canSend && <button className="btn btn-primary btn-sm" type="button" onClick={() => sendApproval(row)} disabled={sending === row.id || !customerEmail}>{sending === row.id ? 'Sending...' : 'Send Approval'}</button>}
                      {canMarkNoApproval && <button className="btn btn-ghost btn-sm" type="button" onClick={() => updateStatus(row, 'approved')} disabled={saving === row.id}>Approve Internally</button>}
                      {canMarkComplete && <button className="btn btn-ghost btn-sm" type="button" onClick={() => updateStatus(row, 'completed')} disabled={saving === row.id}>Mark Completed</button>}
                      {canMarkComplete && <button className="btn btn-ghost btn-sm" type="button" onClick={() => updateStatus(row, 'not_completed')} disabled={saving === row.id}>Not Completed</button>}
                      {row.status === 'draft' && <button className="btn btn-danger btn-sm" type="button" onClick={() => voidRequest(row)} disabled={saving === row.id}>Void</button>}
                    </div>
                    {(canMarkComplete || row.status === 'approved') && (
                      <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
                        <label>Technician completion comment</label>
                        <input
                          value={completionForms[row.id] || ''}
                          onChange={e => setCompletionForms(current => ({ ...current, [row.id]: e.target.value }))}
                          placeholder="Required before marking completed or not completed."
                        />
                      </div>
                    )}
                    {evidenceRows.length > 0 && (
                      <details style={{ marginTop: 10 }}>
                        <summary className="fs12 text-muted">Linked evidence ({linked.size})</summary>
                        <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                          {evidenceRows.map(evidence => (
                            <label key={evidence.id} className="check-row" style={{ justifyContent: 'space-between' }}>
                              <span>
                                <input
                                  type="checkbox"
                                  checked={linked.has(evidence.id)}
                                  onChange={() => toggleEvidence(row, evidence)}
                                  disabled={saving === `evidence-${row.id}-${evidence.id}`}
                                /> {evidenceTitle(evidence)}
                              </span>
                              <span className={`job-status-chip job-status-${evidence.visibility === 'customer' ? 'green' : 'muted'}`}>
                                {evidence.visibility === 'customer' ? 'Customer report' : 'Internal only'}
                              </span>
                            </label>
                          ))}
                        </div>
                      </details>
                    )}
                    <details style={{ marginTop: 10 }}>
                      <summary className="fs12 text-muted">Stock linked to this work</summary>
                      <div style={{ marginTop: 8 }}>
                        <JobStockUsagePanel
                          jobId={jobId}
                          workshopId={workshopId}
                          mode="edit"
                          sourceType="general_additional_work"
                          additionalWorkRequestId={row.id}
                          compact
                          onChanged={() => {
                            load()
                            if (onChanged) onChanged()
                          }}
                        />
                      </div>
                    </details>
                  </>
                )}
              </div>
            )
          })}
        </div>

        {rows.length > 0 && (
          <div className="detail-row" style={{ marginTop: 12 }}>
            <span className="detail-label">Billable additional work</span>
            <span className="detail-val">{formatServiceMoney(additionalWorkBillableTotal(rows))}</span>
          </div>
        )}
      </div>
    </>
  )
}
